import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'selectVariant',
})(({ theme, selectVariant }) => {
  const variants = {
    default: {
      // Uses theme defaults
    },
    auth: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(2),
        fontSize: '1.1rem',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
        },
        '&.Mui-focused': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: '1.1rem',
        fontWeight: 500,
      },
    },
    form: {
      '& .MuiOutlinedInput-root': {
        fontSize: '0.95rem',
        borderRadius: 0,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${theme.palette.primary.main}15`,
        },
        '&.Mui-focused': {
          transform: 'translateY(-1px)',
        },
      },
    },
    search: {
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(3),
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        '&:hover': {
          backgroundColor: '#ffffff',
          boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
        },
        '&.Mui-focused': {
          backgroundColor: '#ffffff',
          boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
        },
      },
    },
    compact: {
      '& .MuiOutlinedInput-root': {
        fontSize: '0.875rem',
        '& .MuiSelect-select': {
          padding: '8px 12px',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
  };

  return variants[selectVariant] || variants.default;
});

const EasyCheckSelect = React.forwardRef(({ 
  variant = 'default',
  label,
  error,
  helperText,
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  children,
  ...props 
}, ref) => {
  const renderOptions = () => {
    if (children) return children;
    
    return options.map((option, index) => {
      const value = typeof option === 'object' ? option[valueKey] : option;
      const displayLabel = typeof option === 'object' ? option[labelKey] : option;
      
      return (
        <MenuItem key={value || index} value={value}>
          {displayLabel}
        </MenuItem>
      );
    });
  };

  return (
    <StyledFormControl
      variant="outlined"
      fullWidth
      error={error}
      selectVariant={variant}
      {...props}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        ref={ref}
        label={label}
        {...props}
      >
        {renderOptions()}
      </Select>
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
});

EasyCheckSelect.propTypes = {
  variant: PropTypes.oneOf(['default', 'auth', 'form', 'search', 'compact']),
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  options: PropTypes.array,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  children: PropTypes.node,
};

EasyCheckSelect.displayName = 'EasyCheckSelect';

export default EasyCheckSelect; 